import React, { useState, useEffect } from 'react';
import { Search, Plus, SortAsc, SortDesc } from 'lucide-react';
import Header from './Header';
import { getUserInfo } from './apiUtils';
import CreateUserDialog from './ManageUsersComponents/CreateUserDialog';
import UpdatePasswordDialog from './ManageUsersComponents/UpdatePasswordDialog';
import UpdateStatusDialog from './ManageUsersComponents/UpdateStatusDialog';
import UserActionsPanel from './ManageUsersComponents/UserActionsPanel';
import Dialog from './ManageUsersComponents/Dialog';
import UpdateAccessLevelDialog from './ManageUsersComponents/UpdateAccessLevelDialog';
import ManageProjectAccessDialog from './ManageUsersComponents/ManageProjectAccessDialog';
// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';


const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '0px',
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    fontSize: '0.875rem',
  },
  th: {
    backgroundColor: '#E5E4E2',
    color: '#0064e5',
    padding: '6px 12px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    fontSize: '0.875rem',
    lineHeight: '1',
    height: '32px',
    cursor: 'pointer',
    userSelect: 'none',
    position: 'relative'
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #ddd',
  },
  checkbox: {
    marginRight: '10px',
  },
  button: {
    padding: '6px 12px',
    margin: '0 5px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  error: {
    color: 'red',
    padding: '10px',
    marginBottom: '10px',
  },
};

const ManageUsers = () => {
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [permissions, setPermissions] = useState({
    canUpdate: false,
    canViewUserList: false,
    canViewUserProjectList: false,
    canAssignProjects: false,
    canViewOtherAccounts: false,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  // Dialog states
  const [isUpdatePasswordDialogOpen, setIsUpdatePasswordDialogOpen] = useState(false);
  const [isUpdateStatusDialogOpen, setIsUpdateStatusDialogOpen] = useState(false);

  const login_token = localStorage.getItem('authToken');
  const [corporateAccount, setCorporateAccount] = useState('');
  const [currentProject, setCurrentProject] = useState('');
  const [userId, setUserId] = useState('');
  const [accountDescription, setAccountDescription] = useState('');
  const [userName, setUserName] = useState('');
  const [redirectPath, setRedirectPath] = useState('');

  const [isUpdateAccessLevelDialogOpen, setIsUpdateAccessLevelDialogOpen] = useState(false);
  const [isProjectAccessDialogOpen, setIsProjectAccessDialogOpen] = useState(false);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const showErrorDialog = (message, path) => {
    setDialogMessage(message);
    setRedirectPath(path);
    setShowDialog(true);
  };

  const checkPermission = async (categoryHeader, categorySubHeaders) => {
    console.log(`Checking permissions for ${categoryHeader} with sub-headers:`, categorySubHeaders);

    try {
      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: userId,
          project_id: currentProject,
          category_header: categoryHeader,
          category_sub_header: ""
        }),
      });
      const data = await response.json();
      console.log(`API response for ${categoryHeader}:`, data);

      const newPermissions = {};

      data.user_actions_list.forEach(item => {
        console.log(`JK Processing user action: ${item.user_action}, has_access: ${item.has_access}`);

        switch(item.user_action) {
          case 'Create Or Edit Users':
            newPermissions.canUpdate = item.has_access;
            break;
          case 'View User List':
            newPermissions.canViewUserList = item.has_access;
            console.log(`User has permission to view user list: ${item.has_access}`);
            break;
          case 'Assign Projects To Users':
            newPermissions.canAssignProjects = item.has_access;
            console.log(`User has permission to assign projects: ${item.has_access}`);
            break;
          case 'View User Project List':
            newPermissions.canViewUserProjectList = item.has_access;
            console.log(`User has permission to view user projects: ${item.has_access}`);
            break;
          case 'View Other Accounts List':
            newPermissions.canViewOtherAccounts = item.has_access;
            console.log(`User has permission to view other accounts list: ${item.has_access}`);
            break;
        }
      });

      setPermissions(prevPermissions => ({
        ...prevPermissions,
        ...newPermissions
      }));

      // Return the new permissions so they can be used immediately
      return {
        success: data.access_status && data.status === 'Success',
        permissions: newPermissions
      };
    } catch (error) {
      console.error(`Error checking permission for ${categoryHeader}:`, error);
      return {
        success: false,
        permissions: {}
      };
    }
  };

  const handleDefineProjectAccess = () => {
    if (!permissions.canViewUserProjectList) {
      setDialogMessage('You do not have permission to manage project access');
      setShowDialog(true);
      return;
    }

    if (selectedUsers.size === 0) {
      setDialogMessage('Please select at least one user to manage project access');
      setShowDialog(true);
      return;
    }

    setIsProjectAccessDialogOpen(true);
  };

  const fetchAccounts = async (canViewOtherAccounts) => {
    try {
      const response = await fetch(appConfig.getUrl('/get_account_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          user_id: canViewOtherAccounts ? '' : userId,
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setAccounts(data.account_list);
        console.log('Accounts fetched successfully:', data.account_list.length, 'accounts');
      } else {
        console.error('Failed to fetch accounts:', data.status_description);
        setAccounts([]);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([]);
    }
  };

  const fetchUsers = async (accountId) => {
    setUsers([]);

    console.log('Fetching users for account:', accountId);

    if (!permissions.canViewUserList) {
      console.log('User does not have permission to view user list');
      return;
    }

    if (!accountId) {
      return;
    }

    try {
      const response = await fetch(appConfig.getUrl('/get_user_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          requesting_for_corporate_account: accountId,
          user_status: ""
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        let filteredUsers = data.user_list;

        if (searchFilter) {
                filteredUsers = filteredUsers.filter(user =>
                  user.user_name.toLowerCase().includes(searchFilter.toLowerCase()) ||
                  user.user_id.toLowerCase().includes(searchFilter.toLowerCase()) ||
                  (user.business_team_description && user.business_team_description.toLowerCase().includes(searchFilter.toLowerCase())) ||
                  (user.user_role && user.user_role.toLowerCase().includes(searchFilter.toLowerCase()))
                );
        }

        if (sortConfig.key) {
          filteredUsers.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (aValue < bValue) {
              return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
              return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
          });
        }

        setUsers(filteredUsers);
      } else {
        console.error('Failed to fetch users:', data.status_description);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const handleAccountSelect = (accountId) => {
    setSelectedAccountId(accountId);
    setSelectedUsers(new Set());
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearch = () => {
    setSearchFilter(searchInput);
  };

  const handleSearchInputChange = (e) => {
    const newValue = e.target.value;
    setSearchInput(newValue);

    if (newValue === '') {
      setSearchFilter('');
    }
  };

  const getSelectedAccountDescription = () => {
    if (selectedAccountId) {
      const account = accounts.find(acc => acc.corporate_account === selectedAccountId);
      return account ? account.account_description : '';
    }
    return '';
  };

  const refreshUsers = () => {
    if (permissions.canViewUserList && selectedAccountId) {
      fetchUsers(selectedAccountId);
    }
    setSelectedUsers(new Set());
  };

  // Initial load useEffect - Get user info
  useEffect(() => {
    const setInitialLoad = async () => {
      if (!login_token) {
        showErrorDialog('Invalid login credentials. Please login first', '/LoginAndAuthentication');
        return;
      }

      try {
        const result = await getUserInfo({
          login_token: login_token
        });

        if (result.success) {
          setUserId(result.user_id);
          setUserName(result.userInfo.user_name);
          setCorporateAccount(result.userInfo.corporate_account);
          setAccountDescription(result.userInfo.account_description);
          setCurrentProject(result.userInfo.default_project || 'default');
        } else {
          showErrorDialog('Invalid login credentials. Please login again', '/LoginAndAuthentication');
        }
      } catch (error) {
        console.error('Error calling API:', error);
      }
    };

    setInitialLoad();
  }, []);

  // Main initialization useEffect - Check permissions and fetch accounts
  useEffect(() => {
    const initializeApp = async () => {
      if (!userId || !corporateAccount) return;

      try {
        console.log('Starting app initialization...');

        // Clear accounts first to prevent race conditions
        setAccounts([]);

        // Step 1: Check admin permissions
        console.log('Checking admin permissions...');
        const adminResult = await checkPermission('Admin Functions', ['Create Or Edit Users', 'View User List']);

        // Step 2: Check super admin permissions
        console.log('Checking super admin permissions...');
        const superAdminResult = await checkPermission('Super Admin Functions', ['View Other Accounts List']);

        // Step 3: Fetch accounts using the most recent permission result
        console.log('Fetching accounts with permissions:', {
          canViewOtherAccounts: superAdminResult.permissions?.canViewOtherAccounts
        });

        await fetchAccounts(superAdminResult.permissions?.canViewOtherAccounts || false);

        console.log('App initialization complete');

      } catch (error) {
        console.error('Error initializing app:', error);
      }
    };

    initializeApp();
  }, [userId, corporateAccount]);

  // Users fetching useEffect
  useEffect(() => {
    console.log('useEffect for selectedAccountId, searchFilter, sortConfig, permissions.canViewUserList triggered --', selectedAccountId);
    console.log('can View List Permission:', permissions.canViewUserList);

    if (selectedAccountId && permissions.canViewUserList) {
      console.log('Fetching users for selected account:', selectedAccountId);
      fetchUsers(selectedAccountId);
    } else if (selectedAccountId && !permissions.canViewUserList) {
      setUsers([]);
    }
  }, [selectedAccountId, searchFilter, sortConfig, permissions.canViewUserList]);

  // Auto-select account if only one exists
  useEffect(() => {
    if (accounts.length === 1 && !selectedAccountId) {
      const singleAccount = accounts[0];
      console.log('Auto-selecting single account:', singleAccount.corporate_account);
      setSelectedAccountId(singleAccount.corporate_account);
      setSelectedUsers(new Set());
      setCurrentPage(1);
    }
  }, [accounts, selectedAccountId]);

  // Pagination useEffects
  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilter, sortConfig]);

  useEffect(() => {
    const calculatedTotalPages = Math.ceil(users.length / itemsPerPage);
    if (currentPage > Math.max(1, calculatedTotalPages)) {
      setCurrentPage(Math.max(1, calculatedTotalPages));
    }
  }, [users, currentPage, itemsPerPage]);

  // Add pagination calculation logic
  const totalPages = Math.ceil(users.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = users.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div style={{ marginTop: '80px' }}>
        {/* Header section */}
        <div className="pt-2 pb-2">
          <div className="flex items-center px-6 mb-0 justify-between">
            <div className="w-80">
              <div className="flex items-center relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  placeholder="Search users..."
                  className="p-2 pr-4 border rounded w-full"
                />
                <button
                  onClick={handleSearch}
                  className="ml-2 p-1 rounded bg-blue-500 hover:bg-blue-600 text-white"
                  aria-label="Search"
                >
                  <Search size={16} />
                </button>
              </div>
            </div>

            <h1 className="text-xl font-bold text-center flex-1">Manage Users</h1>

            <div className="w-80"></div>
          </div>

          <div className="flex items-center px-6 mb-0 bg-blue-100 py-0 rounded-lg">
            {/* Selected Account Path - Left aligned */}
            <div className="flex-1 text-left">
              <span className="text-base font-bold text-blue-700">Selected Account: </span>
              <span className="text-sm">
                {selectedAccountId ? (
                  <>
                    <span className="text-green-600">
                      {getSelectedAccountDescription()} ({selectedAccountId})
                    </span>
                    <button
                      onClick={() => {
                        setSelectedAccountId(null);
                        setUsers([]);
                      }}
                      className="px-3 py-1 text-xs bg-red-300 text-white rounded hover:bg-red-500 items-center ml-4">
                      X Clear Selection
                    </button>
                  </>
                ) : (
                  <span style={{ color: 'red' }}>None Selected</span>
                )}
              </span>
            </div>

            {/* Current User Info - Center aligned */}
            <div className="flex-1 text-center">
              <h2 className="text-base font-bold text-blue-700">Account Level User List</h2>
            </div>

            {/* Create User Button - Right aligned */}
            <div className="flex-1 flex justify-end items-center relative group">
              <button
                disabled={!selectedAccountId || !permissions.canUpdate}
                onClick={() => {
                  if (selectedAccountId && permissions.canUpdate) {
                    setSelectedUserForEdit(null);
                    setIsEditMode(false);
                    setIsCreateDialogOpen(true);
                  } else if (!selectedAccountId) {
                    setDialogMessage('Please select an account from the left panel first');
                    setShowDialog(true);
                  } else if (!permissions.canUpdate) {
                    setDialogMessage('You do not have permission to create or edit users');
                    setShowDialog(true);
                  }
                }}
                className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${(!selectedAccountId || !permissions.canUpdate) ? 'opacity-50' : ''}`}
              >
                <Plus size={18} className="mr-2" />
                <span>Create New User</span>
              </button>

              {(!permissions.canUpdate || !selectedAccountId) && (
                <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                  {!selectedAccountId
                    ? "Select an account from the left panel to enable this button"
                    : !permissions.canUpdate
                    ? "You do not have permission to create or edit users"
                    : ""}
                  <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-64px)]">
        {/* Left Panel - Accounts */}
        <div className="mt-0 w-64 text-white bg-blue-800 border-r overflow-y-auto">
          <div className="p-4">
            <div className="flex justify-center items-center mb-4 bg-white p-2 rounded-lg shadow">
              <h2 className="font-semibold text-sm text-blue-600">Select Account</h2>
            </div>

            {accounts.map(account => (
              <div
                key={account.corporate_account}
                className={`flex items-center py-2 cursor-pointer ${
                  selectedAccountId === account.corporate_account ? 'bg-blue-100' : ''
                }`}
                style={{
                  backgroundColor: selectedAccountId === account.corporate_account ? '#DBEAFE' : 'transparent',
                  color: selectedAccountId === account.corporate_account ? '#1E40AF' : 'white',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedAccountId !== account.corporate_account) {
                    e.currentTarget.style.backgroundColor = '#fcfafc';
                    e.currentTarget.style.color = '#007bff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedAccountId !== account.corporate_account) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'white';
                  }
                }}
                onClick={() => handleAccountSelect(account.corporate_account)}
              >
                <span className="ml-2 text-sm">
                  {account.account_description} ({account.corporate_account})
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-4">
          {/* Main table area */}
          <div className="flex-1 overflow-y-auto p-0 relative">
            {!permissions.canViewUserList ? (
              <div className="flex items-center justify-center h-full">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 max-w-md mx-auto">
                  <div className="flex items-center justify-center mb-4">
                    <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-yellow-800 mb-3 text-center">Access Restricted</h3>
                  <p className="text-yellow-700 text-center">
                    You do not have permission to view the user list. Please contact your administrator to request access.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex-1">
                    <div className="bg-white rounded-lg shadow overflow-x-auto">
                  <table style={styles.table}>
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th style={styles.th}>
                          <input
                            type="checkbox"
                            checked={users.length > 0 && selectedUsers.size === users.length}
                            onChange={(e) => {
                              const newSelected = new Set();
                              if (e.target.checked) {
                                users.forEach(user => newSelected.add(user.user_id));
                              }
                              setSelectedUsers(newSelected);
                            }}
                          />
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('user_id')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>User ID</span>
                            <span className="ml-1">
                              {sortConfig.key === 'user_id' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('user_name')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>User Name</span>
                            <span className="ml-1">
                              {sortConfig.key === 'user_name' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('access_level')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Access Level</span>
                            <span className="ml-1">
                              {sortConfig.key === 'access_level' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('status')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Status</span>
                            <span className="ml-1">
                              {sortConfig.key === 'status' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('default_project')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Default Project</span>
                            <span className="ml-1">
                              {sortConfig.key === 'default_project' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>


                        <th
                          style={styles.th}
                          onClick={() => handleSort('business_team_description')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Business Team</span>
                            <span className="ml-1">
                              {sortConfig.key === 'business_team_description' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>



                        <th
                          style={styles.th}
                          onClick={() => handleSort('user_role')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>User Role</span>
                            <span className="ml-1">
                              {sortConfig.key === 'user_role' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>




                        <th
                          style={styles.th}
                          onClick={() => handleSort('created_date')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Created Date</span>
                            <span className="ml-1">
                              {sortConfig.key === 'created_date' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('updated_date')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Updated Date</span>
                            <span className="ml-1">
                              {sortConfig.key === 'updated_date' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.map(user => (
                        <tr
                          key={user.user_id}
                          className="border-t hover:bg-gray-50"
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedUsers.has(user.user_id)}
                              onChange={() => {
                                const newSelected = new Set(selectedUsers);
                                if (newSelected.has(user.user_id)) {
                                  newSelected.delete(user.user_id);
                                } else {
                                  newSelected.add(user.user_id);
                                }
                                setSelectedUsers(newSelected);
                              }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="relative group">
                              <button
                                className={`text-blue-600 hover:text-blue-800 hover:underline focus:outline-none ${
                                  !permissions.canUpdate ? 'opacity-50 ' : ''
                                }`}
                                onClick={() => {
                                  if (permissions.canUpdate) {
                                    setSelectedUsers(new Set([user.user_id]));
                                    setSelectedUserForEdit(user);
                                    setIsEditMode(true);
                                    setIsCreateDialogOpen(true);
                                  } else {
                                    setDialogMessage('You do not have permission to edit users');
                                    setShowDialog(true);
                                  }
                                }}
                                disabled={!permissions.canUpdate}
                              >
                                {user.user_id}
                              </button>

                              {!permissions.canUpdate && (
                                <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                                  You do not have permission to edit users
                                  <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">{user.user_name}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                              {user.access_level || 'Not set'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              user.status === 'Active' ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {user.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-700">
                              {user.default_project || 'Not set'}
                            </span>
                          </td>


                          <td className="px-4 py-3">
                              <span className="text-sm text-gray-700">
                                {user.business_team_description || 'Not set'}
                              </span>
                            </td>


                            <td className="px-4 py-3">
                              <span className="text-sm text-gray-700">
                                {user.user_role || 'Not set'}
                              </span>
                            </td>


                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-600">
                              {formatDate(user.created_date)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-600">
                              {formatDate(user.updated_date)}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr>
                            <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                            No users found. {selectedAccountId ? 'Create a new user or adjust your search.' : 'Select an account to view users.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination and user count information */}
                {users.length > 0 && (
                  <div className="mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>Total Users: {users.length}</span>
                      {selectedUsers.size > 0 && (
                        <span>| Selected: {selectedUsers.size}</span>
                      )}
                      <span>| Showing {users.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, users.length)} of {users.length}</span>
                    </div>

                    {/* Pagination controls */}
                    <div className="flex items-center gap-2">
                      <button
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                      <button
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages || totalPages === 0}
                        aria-label="Next page"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>

                    {/* Items per page selector */}
                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="border rounded p-1 text-sm"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right sidebar with user actions */}
          <UserActionsPanel
            permissions={permissions}
            selectedUsers={selectedUsers}
            onUpdatePassword={() => setIsUpdatePasswordDialogOpen(true)}
            onUpdateStatus={() => setIsUpdateStatusDialogOpen(true)}
            onUpdateAccessLevel={() => setIsUpdateAccessLevelDialogOpen(true)}
            onDefineProjectAccess={handleDefineProjectAccess}
          />
        </div>
      </div>

      {/* Dialog components */}
      {showDialog && (
        <Dialog
          message={dialogMessage}
          onClose={() => {
            setShowDialog(false);
          }}
          onCancel={() => setShowDialog(false)}
        />
      )}

      <CreateUserDialog
        isOpen={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setIsEditMode(false);
          setSelectedUserForEdit(null);
        }}
        onSubmit={refreshUsers}
        selectedAccountId={selectedAccountId}
        isEditMode={isEditMode}
        userToEdit={selectedUserForEdit}
        permissions={permissions}
        loginToken={login_token}
        corporateAccount={corporateAccount}
        currentProject={currentProject}
      />

      <UpdatePasswordDialog
        isOpen={isUpdatePasswordDialogOpen}
        onClose={(success) => {
          setIsUpdatePasswordDialogOpen(false);
          if (success) {
            setSelectedUsers(new Set());
          }
        }}
        selectedUserIds={selectedUsers}
        corporateAccount={corporateAccount}
        selectedAccountId={selectedAccountId}
        login_token={login_token}
        permissions={permissions}
      />

      <UpdateStatusDialog
        isOpen={isUpdateStatusDialogOpen}
        onClose={(success) => {
          setIsUpdateStatusDialogOpen(false);
          if (success) {
            refreshUsers();
            setSelectedUsers(new Set());
          }
        }}
        selectedUserIds={selectedUsers}
        corporateAccount={corporateAccount}
        selectedAccountId={selectedAccountId}
        login_token={login_token}
        permissions={permissions}
        currentProject={currentProject}
      />

      <UpdateAccessLevelDialog
        isOpen={isUpdateAccessLevelDialogOpen}
        onClose={(success) => {
          setIsUpdateAccessLevelDialogOpen(false);
          if (success) {
            refreshUsers();
            setSelectedUsers(new Set());
          }
        }}
        selectedUserIds={selectedUsers}
        corporateAccount={corporateAccount}
        selectedAccountId={selectedAccountId}
        login_token={login_token}
        permissions={permissions}
      />

      <ManageProjectAccessDialog
        isOpen={isProjectAccessDialogOpen}
        onClose={(success) => {
          setIsProjectAccessDialogOpen(false);
          if (success) {
            // Optionally refresh users list or show confirmation
            setSelectedUsers(new Set());
          }
        }}
        selectedUserIds={selectedUsers}
        corporateAccount={corporateAccount}
        selectedAccountId={selectedAccountId}
        login_token={login_token}
        permissions={permissions}
      />
    </div>
  );
};

export default ManageUsers;